import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Tutoring.css';
import { FaGraduationCap, FaCode, FaBrain, FaRocket, FaUsers, FaCalendarAlt, FaClock, FaFire, FaGift, FaPercent, FaStar, FaCheckCircle, FaLaptopCode, FaChartLine, FaQuestionCircle, FaSchool, FaUserGraduate, FaGlobe, FaChevronDown, FaChevronUp } from 'react-icons/fa';


const Tutoring = () => {
    const navigate = useNavigate();
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [loadingProduct, setLoadingProduct] = useState(null);

    // Handle Stripe checkout
    const handleCheckout = async (productType) => {
        // For hourly sessions, redirect to booking page first
        if (['math', 'coding', 'cs'].includes(productType)) {
            navigate(`/book-time?type=${productType}`);
            return;
        }

        setLoadingProduct(productType);
        try {
            const response = await fetch('/api/create-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productType, quantity: 1 })
            });
            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                alert('Error creating checkout session');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Error connecting to payment system');
        } finally {
            setLoadingProduct(null);
        }
    };

    // Program start date: Feb 28, 2026
    const programStartDate = useMemo(() => new Date('2026-02-28T00:00:00'), []);

    // Early bird deadline: 15 days before start (50% off until then)
    const earlyBirdDeadline = useMemo(() => {
        const deadline = new Date(programStartDate);
        deadline.setDate(deadline.getDate() - 15);
        return deadline;
    }, [programStartDate]);

    // Calculate days until program start for scarcity
    const daysUntilStart = useMemo(() => {
        const now = new Date();
        return Math.ceil((programStartDate - now) / (1000 * 60 * 60 * 24));
    }, [programStartDate]);

    // Scarcity: seats left = daysLeft * 5 (deterministic, no randomness)
    const seatsLeft = useMemo(() => {
        return Math.max(5, daysUntilStart * 5);
    }, [daysUntilStart]);

    // Check if early bird is still active
    const isEarlyBird = useMemo(() => {
        return new Date() < earlyBirdDeadline;
    }, [earlyBirdDeadline]);

    // Countdown timer for early bird
    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            const diff = earlyBirdDeadline - now;

            if (diff <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            setTimeLeft({
                days: Math.floor(diff / (1000 * 60 * 60 * 24)),
                hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((diff / (1000 * 60)) % 60),
                seconds: Math.floor((diff / 1000) % 60)
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [earlyBirdDeadline]);

    const hourlyServices = [
        {
            id: 'math',
            icon: <FaGraduationCap />,
            title: 'Mathematics',
            subtitle: 'Grade 3 to Linear Algebra',
            price: 32,
            features: ['Elementary to Advanced', 'SAT/ACT Prep', 'College-Level Math', 'Personalized Curriculum']
        },
        {
            id: 'coding',
            icon: <FaCode />,
            title: 'Coding',
            subtitle: 'Fundamentals & Basics',
            price: 32,
            features: ['Python & JavaScript', 'Web Development Intro', 'Problem Solving', 'Project-Based Learning']
        },
        {
            id: 'cs',
            icon: <FaBrain />,
            title: 'Computer Science',
            subtitle: 'DSA & Leetcode',
            price: 32,
            features: ['Data Structures', 'Algorithm Design', 'Interview Prep', 'Competitive Programming']
        },

    ];

    const programs = [
        {
            id: 'fullstack',
            icon: <FaRocket />,
            title: 'Full Stack Web Development',
            description: 'Master modern web development from frontend to backend',
            originalPrice: 1000,
            price: 500,
            schedule: 'Feb 28 - May 2',
            time: 'Every Saturday, 11 AM - 2 PM CT',
            features: ['React & Next.js', 'Node.js & Express', 'Database Design', 'Deployment & DevOps', 'Portfolio Projects']
        },
        {
            id: 'aiml',
            icon: <FaBrain />,
            title: 'AI + Machine Learning',
            description: 'Dive into the future of technology with hands-on AI projects',
            originalPrice: 1000,
            price: 500,
            schedule: 'Feb 28 - May 2',
            time: 'Every Saturday, 11 AM - 2 PM CT',
            features: ['Python for ML', 'Neural Networks', 'Computer Vision', 'NLP Fundamentals', 'Real-World Projects']
        },

    ];



    const successStories = [
        'Stanford University',
        'MIT',
        'Harvard University',
        'Yale University',
        'Princeton University',
        'Columbia University',
        'University of Michigan',
        'Carnegie Mellon University',
        'University of California, Berkeley',
        'University of California, Los Angeles'
    ];

    // Stats for impact section
    const stats = [
        { value: '850+', label: 'Students Taught', icon: <FaUsers /> },
        { value: '60%', label: 'Ivy League Acceptance Rate', icon: <FaGraduationCap /> },
        { value: '4.9/5', label: 'Average Rating', icon: <FaStar /> },
        { value: '4+', label: 'Years Teaching', icon: <FaCalendarAlt /> }
    ];

    // Who is this for
    const whoIsThisFor = [
        {
            icon: <FaSchool />,
            title: 'High School Students (Grades 9-12)',
            description: 'Preparing for AP exams, SAT/ACT, or looking to build a strong foundation for college'
        },
        {
            icon: <FaUserGraduate />,
            title: 'Parents & Students Preparing for College',
            description: 'Building standout applications with projects, test scores, and demonstrable skills'
        },
        {
            icon: <FaCode />,
            title: 'Aspiring Developers',
            description: 'Anyone wanting to break into tech with structured, project-based learning'
        },
        {
            icon: <FaGlobe />,
            title: 'International Students',
            description: 'All sessions conducted online - learn from anywhere in the world'
        }
    ];

    // Curriculum highlights
    const curriculumTopics = [
        {
            icon: <FaLaptopCode />,
            title: 'Web Development',
            items: ['React & Next.js', 'Node.js backends', 'Database design', 'API development']
        },
        {
            icon: <FaBrain />,
            title: 'AI & Machine Learning',
            items: ['Python fundamentals', 'Neural networks', 'Computer vision', 'NLP basics']
        },
        {
            icon: <FaChartLine />,
            title: 'Data Structures & Algorithms',
            items: ['Arrays & strings', 'Trees & graphs', 'Dynamic programming', 'System design']
        },
        {
            icon: <FaGraduationCap />,
            title: 'Math Foundations',
            items: ['Algebra to Calculus', 'Linear algebra', 'Statistics', 'Discrete math']
        }
    ];

    // FAQ
    const [openFaq, setOpenFaq] = useState(null);
    const faqs = [
        {
            q: 'What prior experience do I need?',
            a: 'No prior programming experience required for our beginner programs. We have cohorts for all skill levels, from complete beginners to advanced students preparing for technical interviews.'
        },
        {
            q: 'How are sessions conducted?',
            a: 'All sessions are conducted live via Zoom with screen sharing. You\'ll get personalized attention, not pre-recorded videos. Sessions can be 1-on-1 or small group (max 4 students).'
        },
        {
            q: 'What happens after I pay?',
            a: 'For hourly sessions, you\'ll be redirected to schedule your first session immediately. For programs, you\'ll receive an email within 24 hours with onboarding materials and access to our Discord community.'
        },
        {
            q: 'Can I get a refund?',
            a: 'Yes! If you\'re not satisfied after your first session or first week of the program, we offer a full refund. No questions asked.'
        },
        {
            q: 'How will this help with college applications?',
            a: 'You\'ll build real projects for your portfolio, receive documented progress reports, and can request a letter of recommendation after completing a program. Past students have been accepted to Stanford, MIT, and Ivy League schools.'
        }
    ];

    return (
        <div className="tutoring-container">
            {/* Dynamic Gradient Background */}
            <div className="tutoring-header-gradient" />

            <div className="tutoring-content">
                {/* Hero Section */}
                <section className="tutoring-hero">
                    <div className="hero-badge">
                        <FaStar className="badge-icon" />
                        <span>College Prep + 1-on-1 Tutoring</span>
                    </div>
                    <h1 className="tutoring-title">
                        Level Up Your Skills
                    </h1>
                    <p className="tutoring-subtitle">
                        Learn from industry professionals who've helped students get into Ivy League schools and land top tech jobs.
                    </p>
                </section>

                {/* Stats Banner */}
                <section className="stats-section">
                    <div className="stats-grid">
                        {stats.map((stat, idx) => (
                            <div key={idx} className="stat-card">
                                <span className="stat-icon">{stat.icon}</span>
                                <span className="stat-value">{stat.value}</span>
                                <span className="stat-label">{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Who Is This For */}
                <section id="audience" className="audience-section">
                    <h2 className="section-title-centered">
                        <FaQuestionCircle className="section-icon" />
                        Who Is This For?
                    </h2>
                    <div className="audience-grid">
                        {whoIsThisFor.map((item, idx) => (
                            <div key={idx} className="audience-card">
                                <span className="audience-icon">{item.icon}</span>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Curriculum Topics */}
                <section id="curriculum" className="curriculum-section">
                    <h2 className="section-title-centered">
                        <FaLaptopCode className="section-icon" />
                        What You'll Learn
                    </h2>
                    <div className="curriculum-grid">
                        {curriculumTopics.map((topic, idx) => (
                            <div key={idx} className="curriculum-card">
                                <div className="curriculum-header">
                                    <span className="curriculum-icon">{topic.icon}</span>
                                    <h3>{topic.title}</h3>
                                </div>
                                <ul className="curriculum-items">
                                    {topic.items.map((item, i) => (
                                        <li key={i}>
                                            <FaCheckCircle className="check-icon" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Early Bird Banner */}
                {isEarlyBird && (
                    <section className="early-bird-banner">
                        <div className="early-bird-content">
                            <div className="early-bird-left">
                                <FaFire className="fire-icon" />
                                <div className="early-bird-text">
                                    <h3>Early Bird Special: 50% OFF Programs!</h3>
                                    <p>Lock in your spot now before prices double</p>
                                </div>
                            </div>
                            <div className="countdown-timer">
                                <div className="countdown-item">
                                    <span className="countdown-value">{timeLeft.days}</span>
                                    <span className="countdown-label">Days</span>
                                </div>
                                <div className="countdown-item">
                                    <span className="countdown-value">{String(timeLeft.hours).padStart(2, '0')}</span>
                                    <span className="countdown-label">Hours</span>
                                </div>
                                <div className="countdown-item">
                                    <span className="countdown-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
                                    <span className="countdown-label">Min</span>
                                </div>
                                <div className="countdown-item">
                                    <span className="countdown-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
                                    <span className="countdown-label">Sec</span>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Programs Section */}
                <section id="programs" className="programs-section">
                    <div className="section-header">
                        <h2 className="section-title">
                            <FaRocket className="section-icon" />
                            Immersive Programs
                        </h2>
                        <div className="scarcity-badge">
                            <FaUsers className="scarcity-icon" />
                            <span>Only <strong>{seatsLeft}</strong> seats left!</span>
                        </div>
                    </div>

                    <div className="programs-grid">
                        {programs.map((program) => (
                            <div key={program.id} className="program-card">
                                <div className="program-header">
                                    <span className="program-icon">{program.icon}</span>
                                    <div className="program-badges">
                                        {isEarlyBird && (
                                            <span className="discount-badge">50% OFF</span>
                                        )}
                                    </div>
                                </div>
                                <h3 className="program-title">{program.title}</h3>
                                <p className="program-description">{program.description}</p>

                                <div className="program-schedule">
                                    <div className="schedule-item">
                                        <FaCalendarAlt />
                                        <span>{program.schedule}</span>
                                    </div>
                                    <div className="schedule-item">
                                        <FaClock />
                                        <span>{program.time}</span>
                                    </div>
                                </div>

                                <ul className="program-features">
                                    {program.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <FaCheckCircle className="feature-check" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <div className="program-pricing">
                                    {isEarlyBird ? (
                                        <>
                                            <span className="original-price">${program.originalPrice}</span>
                                            <span className="current-price">${program.price}</span>
                                        </>
                                    ) : (
                                        <span className="current-price">${program.originalPrice}</span>
                                    )}
                                </div>

                                <button
                                    className="enroll-btn"
                                    onClick={() => handleCheckout(program.id)}
                                    disabled={loadingProduct === program.id}
                                >
                                    {loadingProduct === program.id ? 'Processing...' : 'Enroll Now'}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Hourly Services Section */}
                <section id="hourly" className="hourly-section">
                    <div className="section-header">
                        <h2 className="section-title">
                            <FaClock className="section-icon" />
                            Hourly Tutoring
                        </h2>
                        <div className="package-deal">
                            <FaPercent className="package-icon" />
                            <span>Book 5 sessions, get the 6th FREE!</span>
                        </div>
                    </div>

                    <div className="hourly-grid">
                        {hourlyServices.map((service) => (
                            <div key={service.id} className="hourly-card">
                                <span className="hourly-icon">{service.icon}</span>
                                <h3 className="hourly-title">{service.title}</h3>
                                <p className="hourly-subtitle">{service.subtitle}</p>

                                <ul className="hourly-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <FaCheckCircle className="feature-check" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>

                                <div className="hourly-pricing">
                                    <span className="price-value">${service.price}</span>
                                    <span className="price-unit">/hour</span>
                                </div>

                                <button
                                    className="book-btn"
                                    onClick={() => handleCheckout(service.id)}
                                    disabled={loadingProduct === service.id}
                                >
                                    {loadingProduct === service.id ? 'Processing...' : 'Book Session'}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Referral Section */}
                <section id="referral" className="referral-section">
                    <div className="referral-card">
                        <div className="referral-content">
                            <FaGift className="referral-icon" />
                            <div className="referral-text">
                                <h3>Refer a Friend, Get Rewarded!</h3>
                                <p>Know someone who could benefit from tutoring? Refer them and you both get <strong>$25 off</strong> your next session!</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* College Apps Value Section */}
                <section className="college-section">
                    <div className="college-card">
                        <h3>Perfect for College Applications</h3>
                        <p>
                            Demonstrate commitment to learning and academic excellence. Our structured programs and personalized tutoring
                            provide tangible proof of intellectual curiosity that admissions officers love to see.
                        </p>
                        <div className="college-benefits">
                            <div className="benefit">
                                <FaCheckCircle />
                                <span>Documented progress reports</span>
                            </div>
                            <div className="benefit">
                                <FaCheckCircle />
                                <span>Project portfolio development</span>
                            </div>
                            <div className="benefit">
                                <FaCheckCircle />
                                <span>Letter of recommendation support</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Success Banner - Moved to bottom */}
                <section id="success" className="success-banner">
                    <div className="success-content">
                        <div className="success-header">
                            <h3>Proven Track Record</h3>
                        </div>
                        <div className="success-schools">
                            <span className="success-label">Past students admitted to:</span>
                            <div className="school-tags">
                                {successStories.map((school, idx) => (
                                    <span key={idx} className="school-tag">{school}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section id="faq" className="faq-section">
                    <h2 className="section-title-centered">
                        <FaQuestionCircle className="section-icon" />
                        Frequently Asked Questions
                    </h2>
                    <div className="faq-list">
                        {faqs.map((faq, idx) => (
                            <div
                                key={idx}
                                className={`faq-item ${openFaq === idx ? 'open' : ''}`}
                            >
                                <button
                                    className="faq-question"
                                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                >
                                    <span>{faq.q}</span>
                                    {openFaq === idx ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                                {openFaq === idx && (
                                    <div className="faq-answer">
                                        <p>{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Tutoring;
